import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import QuotationLuggageSelectLabel from "./QuotationLuggageSelectLabel";
import { useFormikContext } from "formik";
import AdvancedSelectFormik from "app/pages/.shared/form/AdvancedSelectFormik/AdvancedSelectFormik";
import "./QuotationLuggageSelect.scss";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { BAGGAGE_TYPES } from "app/constants";

const generateSelectOption = ({ baggage, code }) => {
	return {
		luggageInformation: {
			quantity: baggage.quantity,
			maxWeightPerBaggage: baggage.maxWeightPerBaggage,
			price: baggage.pricePerBooking,
			publicPrice: baggage.publicPricePerBooking,
			code: baggage.code,
		},
		code,
	};
};

const generateSelectOptionList = ({ baggageList }) => {
	const noBaggage = {
		quantity: 0,
		maxWeightPerBaggage: 0,
		pricePerBooking: 0,
		publicPricePerBooking: 0,
	};

	return [
		generateSelectOption({
			baggage: noBaggage,
			code: 0,
		}),

		...baggageList.map((baggage, index) =>
			generateSelectOption({
				baggage,
				code: index + 1,
			})
		),
	];
};

const BAGGAGE_FIELD_MAP = {
	[BAGGAGE_TYPES.CABIN_BAGGAGE]: "cabinLuggage",
	[BAGGAGE_TYPES.CHECKED_BAGGAGE]: "luggage",
	[BAGGAGE_TYPES.HAND_BAGGAGE]: "luggage",
	[BAGGAGE_TYPES.TRAIN_BAGGAGE]: "luggage",
};

const getFieldName = luggageType => BAGGAGE_FIELD_MAP[luggageType] ?? "luggage";

const QuotationLuggageSelect = ({
	onBaggageChange,
	flightBaggageOptions = [],
	isTakingTrain = false,
	luggageType = BAGGAGE_TYPES.CHECKED_BAGGAGE,
}) => {
	const { values, touched } = useFormikContext();
	const fieldName = getFieldName(luggageType);
	const currentLuggageOption = values?.[fieldName];

	const options = generateSelectOptionList({ baggageList: flightBaggageOptions });

	return (
		<div className="quotation-luggage-select">
			<AdvancedSelectFormik
				id={fieldName}
				name={fieldName}
				placeholder={
					<Typography
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						variantMD={TYPOGRAPHY_VARIANTS.LARGE}
					>
						<FormattedMessage id="quotation.luggage.select.placeholder" />
					</Typography>
				}
				menuPortalMargin={-20}
				formatOptionLabel={(option, { context }) => {
					return (
						<QuotationLuggageSelectLabel
							isTakingTrain={isTakingTrain}
							price={option?.luggageInformation?.price}
							quantity={option?.luggageInformation?.quantity}
							publicPrice={option?.luggageInformation?.publicPrice}
							isSelected={context === "value"}
							maxWeightPerBaggage={option?.luggageInformation?.maxWeightPerBaggage}
							luggageType={luggageType}
						/>
					);
				}}
				value={options.find(({ luggageInformation }) => {
					// On veut afficher le message "Ajouter des bagages" si il n'a pas interagit avec le select.
					// Cela veut dire qu'il ne veut pas ajouter de bagage.
					// Par contre, on affiche la valeur qu'il a sélectionné si il veut en rajouter

					return (
						luggageInformation.quantity ===
							(touched?.[fieldName] || currentLuggageOption?.quantity > 0
								? currentLuggageOption?.quantity
								: undefined) &&
						luggageInformation.maxWeightPerBaggage ===
							(touched?.[fieldName] || currentLuggageOption?.maxWeightPerBaggage > 0
								? currentLuggageOption?.maxWeightPerBaggage
								: undefined)
					);
				})}
				getOptionValue={({ code }) => code}
				onChange={option => {
					onBaggageChange(option?.luggageInformation);
				}}
				isSearchable={false}
				options={options}
				classNamePrefix="advanced-select__select-quotation-luggage"
			/>
		</div>
	);
};

QuotationLuggageSelect.propTypes = {
	onBaggageChange: PropTypes.func,
	flightBaggageOptions: PropTypes.array,
	isTakingTrain: PropTypes.bool,
	luggageType: PropTypes.oneOf(Object.values(BAGGAGE_TYPES)),
};

export default QuotationLuggageSelect;
